import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { apiClient } from '@services/api';
import { useSessionStore } from '@stores/sessionStore';
import LoadingSpinner from '@components/LoadingSpinner';
import { chromeIntentUrl, detectInAppBrowser, safariUrl, wasHandedOff } from '@utils/inAppBrowser';

// The link every guest message carries: guest.hotel.com/s/<handle>. It carries nothing — opening it sends
// a code to the stay's own phone or email, and only that code lets this browser in. A browser already
// signed in to the stay goes straight to the portal. The code is requested from here (a POST), never by
// loading the URL, so link previewers in WhatsApp / iMessage / mail scanners never trigger one.

type Method = 'whatsapp' | 'sms' | 'email';

interface LinkOption {
  method: Method;
  maskedTo: string;
}

interface LinkState {
  signedIn: boolean;
  hotelSlug?: string;
  hotelName?: string;
  sent?: boolean;
  method?: Method;
  maskedTo?: string;
  retryAfterSeconds?: number | null;
  noContact?: boolean;
  error?: string;
  options?: LinkOption[];
}

interface VerifyData {
  token: string;
  sessionId: string;
  hotelSlug: string;
  hotelName?: string;
  guestName?: string;
  expiresAt?: string;
  checkOutAt?: string;
  hotelTimeZone?: string;
}

// Android: how long to wait for Chrome to take the link before signing in here anyway. If both pages open it,
// the second resumes the same code (the server never sends a new one on re-open).
const CHROME_HANDOFF_MS = 1500;

const METHOD_LABEL: Record<Method, string> = { whatsapp: 'WhatsApp', sms: 'SMS', email: 'email' };

function getDeviceInfo() {
  return {
    deviceName: navigator.userAgent.slice(0, 80),
    screenSize: `${window.screen.width}x${window.screen.height}`,
    language: navigator.language,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    userAgent: navigator.userAgent,
  };
}

function errorOf(err: unknown, fallback: string): string {
  const e = err as { response?: { data?: { error?: string; Error?: string } } };
  return e.response?.data?.error || e.response?.data?.Error || fallback;
}

export default function GuestLinkPage() {
  const { handle = '' } = useParams<{ handle: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  // The portal page the link opens once signed in (the AI's deep links: ?to=folio). A plain path name only.
  const to = searchParams.get('to');
  const page = to && /^[a-z][a-z_-]{0,30}$/.test(to) ? to : 'welcome';
  const { validateSession } = useSessionStore();

  const [state, setState] = useState<LinkState | null>(null);
  const [fatal, setFatal] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [wait, setWait] = useState(0);
  const opened = useRef(false);
  const inApp = useRef(detectInAppBrowser()).current;

  // Resend countdown.
  useEffect(() => {
    if (wait <= 0) return;
    const t = setTimeout(() => setWait((w) => w - 1), 1000);
    return () => clearTimeout(t);
  }, [wait]);

  const apply = (s: LinkState) => {
    setState(s);
    setError(s.error || '');
    setWait(s.retryAfterSeconds ?? 0);
  };

  useEffect(() => {
    if (opened.current) return;
    opened.current = true;
    (async () => {
      // WhatsApp's browser on Android: hand the link to Chrome before any code is sent, so the guest signs in
      // where they will keep using the portal. Once only — a link that came back from Chrome stays here.
      if (inApp === 'android' && !wasHandedOff()) {
        window.location.href = chromeIntentUrl();
        await new Promise((r) => setTimeout(r, CHROME_HANDOFF_MS));
      }
      // A browser that already holds a live session tells the server which — if it is this stay, no code.
      let currentSessionId: string | undefined;
      if (localStorage.getItem('gcxp_token')) {
        await validateSession();
        const { isValid, session } = useSessionStore.getState();
        if (isValid && session) currentSessionId = session.sessionId;
      }
      try {
        const { data } = await apiClient.post<LinkState>(`/api/v1/guest/link/${encodeURIComponent(handle)}/open`, { currentSessionId });
        if (data.signedIn && data.hotelSlug) {
          navigate(`/${data.hotelSlug}/${page}`, { replace: true });
          return;
        }
        apply(data);
      } catch (err) {
        setFatal(errorOf(err, 'This link is invalid or has expired. Ask the hotel to send you a new one.'));
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handle]);

  const send = useCallback(async (method: Method) => {
    setBusy(true);
    setError('');
    setCode('');
    try {
      const { data } = await apiClient.post<LinkState>(`/api/v1/guest/link/${encodeURIComponent(handle)}/send`, { method });
      apply(data);
    } catch (err) {
      setError(errorOf(err, 'Could not send a code. Please try again.'));
    }
    setBusy(false);
  }, [handle]);

  const verify = useCallback(async () => {
    const clean = code.replace(/\D/g, '');
    if (clean.length !== 6) {
      setError('Enter the 6-digit code.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const response = await apiClient.post<Record<string, unknown>>(
        `/api/v1/guest/link/${encodeURIComponent(handle)}/verify`, { code: clean, deviceInfo: getDeviceInfo() });
      const raw = response.data as Record<string, unknown>;
      const d = ((raw.data as Record<string, unknown>) || raw) as unknown as VerifyData;

      // A theme left by another hotel's stay must not leak into this one.
      localStorage.removeItem('gcxp_theme_colors');
      document.documentElement.classList.remove('hotel-themed');

      localStorage.setItem('gcxp_token', d.token);
      localStorage.setItem('gcxp_session_id', d.sessionId);
      localStorage.setItem('gcxp_hotel_slug', d.hotelSlug);
      if (d.expiresAt) localStorage.setItem('gcxp_expires_at', d.expiresAt);
      if (d.checkOutAt) localStorage.setItem('gcxp_checkout_at', d.checkOutAt);
      if (d.hotelTimeZone) localStorage.setItem('gcxp_hotel_timezone', d.hotelTimeZone);

      await validateSession();
      navigate(`/${d.hotelSlug}/${page}`, { replace: true, state: { justActivated: true, guestName: d.guestName } });
    } catch (err) {
      setError(errorOf(err, 'Invalid or expired code.'));
      setBusy(false);
    }
  }, [code, handle, page, navigate, validateSession]);

  const textColor = 'var(--theme-text-color, #212529)';
  const primaryButton = {
    width: '100%', padding: '13px', borderRadius: 10, border: 'none',
    background: 'var(--theme-color-1, #667eea)', color: '#fff', fontWeight: 600, opacity: busy ? 0.7 : 1,
  } as const;
  const linkButton = {
    width: '100%', padding: '10px', background: 'transparent', border: 'none', color: textColor, opacity: 0.75,
  } as const;

  if (fatal) {
    return (
      <div className="d-flex justify-content-center align-items-center min-vh-100" style={{ color: textColor, padding: '1rem' }}>
        <div className="text-center" style={{ maxWidth: 360 }}>
          <div style={{ fontSize: 40, marginBottom: 8 }}>🔗</div>
          <h1 className="h5 mb-2">This link can't be used</h1>
          <p style={{ opacity: 0.75 }}>{fatal}</p>
        </div>
      </div>
    );
  }

  if (!state) {
    return (
      <div className="d-flex flex-column justify-content-center align-items-center min-vh-100">
        <LoadingSpinner />
      </div>
    );
  }

  const others = (state.options || []).filter((o) => o.method !== state.method);

  return (
    <div className="d-flex justify-content-center align-items-center min-vh-100" style={{ color: textColor, padding: '1rem' }}>
      <div className="text-center" style={{ width: '100%', maxWidth: 360 }}>
        {inApp && <OpenInBrowserBar kind={inApp} />}
        <div style={{ fontSize: 40, marginBottom: 8 }}>🔑</div>
        <h1 className="h4 mb-2">{state.hotelName ? `Welcome to ${state.hotelName}` : 'Sign in to your stay'}</h1>

        {state.noContact ? (
          <p style={{ opacity: 0.75, marginTop: 12 }}>
            We have no phone or email to send you a sign-in code. Please ask Reception to let you in.
          </p>
        ) : (
          <>
            <p style={{ opacity: 0.75, marginBottom: 20 }}>
              {state.sent && state.method
                ? <>We sent a sign-in code by {METHOD_LABEL[state.method]} to <span dir="ltr" style={{ whiteSpace: 'nowrap' }}>{state.maskedTo}</span>.</>
                : 'Choose where to send your sign-in code.'}
            </p>

            {state.sent && (
              <>
                <input
                  type="tel"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  autoFocus
                  placeholder="6-digit code"
                  value={code}
                  onChange={(e) => { setCode(e.target.value.replace(/\D/g, '').slice(0, 6)); setError(''); }}
                  onKeyDown={(e) => e.key === 'Enter' && verify()}
                  maxLength={6}
                  style={{ width: '100%', padding: '12px', borderRadius: 10, border: '1px solid rgba(128,128,128,0.4)', background: 'transparent', color: textColor, textAlign: 'center', letterSpacing: 6, fontSize: 20, marginBottom: 10 }}
                />
                {error && <div style={{ color: '#dc3545', fontSize: 14, marginBottom: 8 }}>{error}</div>}
                <button onClick={verify} disabled={busy || code.replace(/\D/g, '').length !== 6} style={primaryButton}>
                  {busy ? 'Checking…' : 'Continue'}
                </button>
                {state.method && (
                  <button onClick={() => send(state.method!)} disabled={busy || wait > 0} style={{ ...linkButton, marginTop: 8 }}>
                    {wait > 0 ? `Send again in ${wait}s` : 'Send the code again'}
                  </button>
                )}
              </>
            )}

            {!state.sent && error && <div style={{ color: '#dc3545', fontSize: 14, marginBottom: 8 }}>{error}</div>}

            {others.map((o) => (
              <button key={o.method} onClick={() => send(o.method)} disabled={busy} style={linkButton}>
                {o.method === 'email'
                  ? <>Email it to <span dir="ltr">{o.maskedTo}</span></>
                  : `Send by ${METHOD_LABEL[o.method]} instead`}
              </button>
            ))}

            <p style={{ opacity: 0.55, fontSize: 12, marginTop: 22 }}>
              By continuing you agree to our <a href="/legal/terms" target="_blank" rel="noopener noreferrer">Terms</a> and{' '}
              <a href="/legal/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>.
            </p>
          </>
        )}
      </div>
    </div>
  );
}

/**
 * Signing in inside WhatsApp's browser works, but on iPhone it keeps nothing once closed: the guest would sign in
 * again in Safari. Offer the real browser — a direct button where the system allows it, else where the menu is.
 */
function OpenInBrowserBar({ kind }: { kind: 'android' | 'ios' }) {
  const box = {
    marginBottom: 20, padding: '12px 14px', borderRadius: 10, fontSize: 14, textAlign: 'start',
    background: 'rgba(128,128,128,0.12)', border: '1px solid rgba(128,128,128,0.25)',
  } as const;
  const button = {
    display: 'inline-block', marginTop: 8, padding: '8px 14px', borderRadius: 8, fontWeight: 600,
    background: 'var(--theme-color-1, #667eea)', color: '#fff', textDecoration: 'none',
  } as const;
  return (
    <div style={box} role="note">
      {kind === 'ios' ? (
        <>
          <div>For the best experience, open this page in Safari — you stay signed in there.</div>
          <a href={safariUrl()} style={button}>Open in Safari</a>
          <div style={{ opacity: 0.7, fontSize: 12, marginTop: 8 }}>
            If nothing happens, tap the ⋯ or share button and choose “Open in browser”.
          </div>
        </>
      ) : (
        <>
          <div>For the best experience, open this page in Chrome — you stay signed in there.</div>
          {!wasHandedOff() && <a href={chromeIntentUrl()} style={button}>Open in Chrome</a>}
          <div style={{ opacity: 0.7, fontSize: 12, marginTop: 8 }}>
            Or tap the ⋮ menu and choose “Open in browser”.
          </div>
        </>
      )}
    </div>
  );
}
